/**
 * File Security Configuration
 * Centralized configuration for file type validation and security checks
 */

/**
 * Blocked file extensions - executable and potentially dangerous files
 * These files are blocked from upload for security reasons
 */
export const BLOCKED_EXTENSIONS = [
  ".bat",
  ".cmd",
  ".com",
  ".pif",
  ".scr",
  ".vbs",
  ".jar",
  ".msi",
  ".dll",
  ".sys",
  ".drv",
  ".cpl",
  ".scf",
  ".lnk",
  ".inf",
  ".reg",
  ".ps1",
  ".psm1",
  ".psd1",
  ".ps1xml",
  ".psc1",
  ".msh",
  ".msh1",
  ".msh2",
  ".mshxml",
  ".msh1xml",
  ".msh2xml",
  ".application",
  ".gadget",
  ".msp",
] as const;

/**
 * Blocked MIME types - dangerous content types
 * Double-check security by validating file content type
 */
export const BLOCKED_MIME_TYPES = [
  "application/x-msdos-program", // DOS executables
  "application/x-executable", // Generic executable
  "application/x-dosexec", // DOS executable
  "application/x-bat", // .bat
  "application/x-sh", // Shell script
  "application/x-shellscript", // Shell script
  "application/x-javascript", // JavaScript
  "application/javascript", // JavaScript
  "text/javascript", // JavaScript
  "application/java-archive", // .jar
  "application/x-msi", // Windows installer
  "application/x-ms-installer", // Windows installer
  "application/vnd.microsoft.portable-executable", // PE files
  "application/x-coff", // COFF executable
  "application/x-sharedlib", // Shared library
  "application/x-dll", // DLL files
  "application/octet-stream", // Generic binary (có thể là executable)
] as const;

/**
 * Viewable file formats - supported for presigned URL viewing
 * These files can be opened directly in browser or external viewer
 */
export const VIEWABLE_FORMATS = [
  "pdf",
  "txt",
  "json",
  "xml",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "js",
  "css",
  "mp4",
  "mp3",
  "m4a",
] as const;

/**
 * Check if file extension is blocked
 * Returns false for files without extension (e.g., Dockerfile, README)
 */
export const isExtensionBlocked = (fileName: string): boolean => {
  if (!fileName || typeof fileName !== "string") return false;

  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === 0) return false; // No extension or hidden file

  const fileExt = fileName.substring(lastDotIndex).toLowerCase();
  return BLOCKED_EXTENSIONS.includes(fileExt as any);
};

/**
 * Check if MIME type is blocked
 */
export const isMimeTypeBlocked = (mimeType: string): boolean => {
  if (!mimeType || typeof mimeType !== "string") return false;

  const normalizedMime = mimeType.toLowerCase();
  return BLOCKED_MIME_TYPES.includes(normalizedMime as any);
};

/**
 * Check if file format is viewable
 */
export const isFormatViewable = (format: string): boolean => {
  if (!format || typeof format !== "string") return false;

  const normalizedFormat = format.toLowerCase();
  return VIEWABLE_FORMATS.includes(normalizedFormat as any);
};

/**
 * Validate file security - returns validation result
 */
export interface FileSecurityValidation {
  isValid: boolean;
  reason?: "extension" | "mime-type";
  blockedValue?: string;
}

export const validateFileSecurity = (
  fileName: string,
  mimeType?: string
): FileSecurityValidation => {
  // Validate input
  if (!fileName || typeof fileName !== "string") {
    return {
      isValid: false,
      reason: "extension",
      blockedValue: "invalid-filename",
    };
  }

  const lastDotIndex = fileName.lastIndexOf(".");

  // Files without extension are allowed (e.g., Dockerfile, README)
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return { isValid: true };
  }

  const fileExt = fileName.substring(lastDotIndex).toLowerCase();

  // Check extension
  if (BLOCKED_EXTENSIONS.includes(fileExt as any)) {
    return {
      isValid: false,
      reason: "extension",
      blockedValue: fileExt,
    };
  }

  // Check MIME type if provided
  if (mimeType && typeof mimeType === "string") {
    const normalizedMime = mimeType.toLowerCase();
    if (BLOCKED_MIME_TYPES.includes(normalizedMime as any)) {
      return {
        isValid: false,
        reason: "mime-type",
        blockedValue: normalizedMime,
      };
    }
  }

  return { isValid: true };
};
